import Flickity from 'flickity'
import { parseJSON } from '@/components/helpers/utils'
const defaultOptions = {
  contain: true
}

const IN_RANGE_MARGIN = '600px'
const OUT_OF_RANGE_MARGIN = '1500px'

export class Carousel extends HTMLElement {
  connectedCallback() {
    this.isInViewRange = false
    this.setupViewportObserver()
    this.setupMutationObserver()
  }

  setupViewportObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInViewRange = true
      this.initializeCarousel()
      return
    }

    this.inViewObserver = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting)
        if (isIntersecting) {
          this.enterViewRange()
        }
      },
      { rootMargin: IN_RANGE_MARGIN }
    )

    this.outOfViewObserver = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting)
        if (!isIntersecting) {
          this.exitViewRange()
        }
      },
      { rootMargin: OUT_OF_RANGE_MARGIN }
    )

    this.inViewObserver.observe(this)
    this.outOfViewObserver.observe(this)
  }

  enterViewRange() {
    if (this.isInViewRange) return
    this.isInViewRange = true
    this.initializeCarousel()
  }

  exitViewRange() {
    if (!this.isInViewRange) return
    this.isInViewRange = false
    this.destroyCarousel()
  }

  initializeCarousel() {
    if (!this.isInViewRange || this.flickity) return

    const computedHeight = this.getBoundingClientRect().height

    if (computedHeight > 0) {
      this.style.minHeight = `${computedHeight}px`
    }

    const options = this.carouselSettings
    options.cellSelector = '&:not(style)'
    const mergedOptions = { ...defaultOptions, ...options }
    if (mergedOptions.wrapAround === true) {
      delete mergedOptions.contain
    }
    if (mergedOptions.autoPlay && mergedOptions.freeScroll === true) {
      mergedOptions.freeScroll = false
    }

    this.withObserverPaused(() => {
      this.flickity = new Flickity(this, mergedOptions)
    })

    this.flickity.on('ready', () => {
      this.readyFrame = requestAnimationFrame(() => {
        const viewport = this.querySelector('.flickity-viewport')
        if (viewport && viewport.style.height) {
          this.readyFrame = requestAnimationFrame(() => {
            if (this.style.minHeight) {
              this.style.minHeight = ''
            }
          })
        }
      })
    })
  }

  destroyCarousel() {
    if (!this.flickity) return
    this.withObserverPaused(() => {
      this.flickity.destroy()
      this.flickity = null
    })
  }

  isFlickityElement(element) {
    if (!element.classList) return false

    return Array.from(element.classList).some((className) => className.startsWith('flickity-'))
  }

  /**
   * Runs a Flickity lifecycle call with our MutationObserver detached. Flickity's
   * activate()/deactivate() reparent every cell; left connected, the observer sees
   * those moves as external changes and schedules another reinit, which self-sustains
   * a full teardown + rebuild every animation frame.
   */
  withObserverPaused(callback) {
    const observer = this.mutationObserver
    if (observer) observer.disconnect()

    try {
      callback()
    } finally {
      if (observer && this.isConnected) {
        observer.takeRecords()
        this.observeChildren()
      }
    }
  }

  observeChildren() {
    this.mutationObserver?.observe(this, {
      childList: true,
      subtree: false
    })
  }

  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      if (this.isReinitializing) return

      let shouldReinitialize = false

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const hasRelevantChanges =
            Array.from(mutation.addedNodes).some(
              (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'STYLE' && !this.isFlickityElement(node)
            ) ||
            Array.from(mutation.removedNodes).some(
              (node) => node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'STYLE' && !this.isFlickityElement(node)
            )

          if (hasRelevantChanges) {
            shouldReinitialize = true
          }
        }
      })

      if (shouldReinitialize) {
        this.reinitializeCarousel()
      }
    })

    this.observeChildren()
  }

  reinitializeCarousel() {
    if (this.isReinitializing) return
    this.isReinitializing = true

    this.destroyCarousel()

    if (this.reinitFrame) cancelAnimationFrame(this.reinitFrame)
    this.reinitFrame = requestAnimationFrame(() => {
      this.reinitFrame = null
      this.initializeCarousel()
      this.isReinitializing = false
    })
  }

  observedAttributes = ['carousel-settings']

  get carouselSettings() {
    const carouselSettings = this.getAttribute('carousel-settings')
    if (!carouselSettings) return {}
    const parsedSettings = parseJSON(carouselSettings)
    if (parsedSettings?.groupCells !== undefined) {
      const value = parsedSettings.groupCells

      if (value === 'true') {
        parsedSettings.groupCells = true
      } else if (value === 'false') {
        parsedSettings.groupCells = false
      } else if (!isNaN(value) && typeof value === 'string') {
        parsedSettings.groupCells = Number(value)
      }
    }
    return parsedSettings
  }

  disconnectedCallback() {
    if (this.reinitFrame) {
      cancelAnimationFrame(this.reinitFrame)
      this.reinitFrame = null
    }

    if (this.readyFrame) {
      cancelAnimationFrame(this.readyFrame)
      this.readyFrame = null
    }

    this.isReinitializing = false

    this.inViewObserver?.disconnect()
    this.inViewObserver = null
    this.outOfViewObserver?.disconnect()
    this.outOfViewObserver = null

    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    if (this.flickity) {
      this.flickity.destroy()
      this.flickity = null
    }
  }
}
